import React, { useEffect, useMemo, useState } from "react";
import { Ic, DomainTag, StatusBadge, Modal, SearchableSelect, AutocompleteTextInput, inputStyle, btnPrimary, btnSecondary, btnGhost, DAYS_HEB, fmtDate, showStyledAlert, showStyledConfirm } from "../components/UI";
import { addRecurringActivity, updateRecurringActivity, deleteRecurringActivity } from "../utils/firestore";
import { formatContributionCurrency, getActivityContributionSummary, getActivityContributionValueTotal, normalizeActivityContributions } from "../utils/activityContributions";

function useMaps(volunteers, domains) {
  const volMap = {};
  volunteers.forEach((v) => {
    volMap[v.id] = v;
  });
  const domMap = {};
  domains.forEach((d) => {
    domMap[d.id] = d;
  });
  return { volMap, domMap };
}

function ApprovalBadge({ status }) {
  const isApproved = status === "approved";
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      background: isApproved ? "#ECFDF5" : "#FFF7ED",
      color: isApproved ? "#166534" : "#C2410C",
      border: `1px solid ${isApproved ? "#BBF7D0" : "#FED7AA"}`,
    }}>
      {isApproved ? "מאושר" : "ממתין לאישור"}
    </span>
  );
}

export default function RecurringPage({ recurring, volunteers, domains, beneficiaries = [], onRefresh, showToast, isMobile = false, addOpenToken = 0, contributionTypes = [], canAddRecurring = true, canApproveRecurring = false, currentUser = null, onQuickAddComplete }) {
  const [view, setView] = useState("list");
  const [modal, setModal] = useState(null);
  const [openedFromQuickAction, setOpenedFromQuickAction] = useState(false);
  const [volunteerFilter, setVolunteerFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [dayFilter, setDayFilter] = useState("all");
  const [domainFilter, setDomainFilter] = useState("all");
  const { volMap, domMap } = useMaps(volunteers, domains);
  const activeDomains = useMemo(() => domains.filter((d) => d.active !== false), [domains]);
  const cityOptions = useMemo(() => {
    const cities = new Set();
    recurring.forEach((item) => {
      const city = (item.city || domMap[item.domainId]?.city || "").trim();
      if (city) cities.add(city);
    });
    return Array.from(cities).sort((a, b) => a.localeCompare(b, "he"));
  }, [recurring, domMap]);
  const filteredRecurring = useMemo(() => {
    return recurring.filter((item) => {
      if (volunteerFilter !== "all" && (item.volunteerId || "") !== volunteerFilter) return false;
      if (cityFilter !== "all" && (item.city || domMap[item.domainId]?.city || "").trim() !== cityFilter) return false;
      if (dayFilter !== "all" && Number(item.day) !== Number(dayFilter)) return false;
      if (domainFilter !== "all" && (item.domainId || "") !== domainFilter) return false;
      return true;
    });
  }, [recurring, volunteerFilter, cityFilter, dayFilter, domainFilter, domMap]);
  const pendingApprovalCount = useMemo(
    () => recurring.filter((item) => (item?.approvalStatus || "pending") !== "approved").length,
    [recurring]
  );

  useEffect(() => {
    if (addOpenToken > 0) {
      setOpenedFromQuickAction(true);
      setModal("add");
    }
  }, [addOpenToken]);

  const handleAdd = async (form) => {
    try {
      const isSuperAdmin = currentUser?.isSuperAdmin === true;
      await addRecurringActivity({
        ...form,
        approvalStatus: isSuperAdmin ? "approved" : "pending",
        status: isSuperAdmin ? "active" : "inactive",
      });
      showToast(isSuperAdmin ? "פעילות קבועה נוספה" : "פעילות קבועה נוספה וממתינה לאישור");
      setModal(null);
      if (openedFromQuickAction && typeof onQuickAddComplete === "function") onQuickAddComplete();
      setOpenedFromQuickAction(false);
      onRefresh();
    } catch (e) {
      await showStyledAlert(e.message);
    }
  };

  const handleEdit = async (form) => {
    try {
      const isSuperAdmin = currentUser?.isSuperAdmin === true;
      await updateRecurringActivity(form.id, {
        ...form,
        approvalStatus: isSuperAdmin ? "approved" : "pending",
        status: isSuperAdmin ? (form.status || "active") : "inactive",
      });
      showToast(isSuperAdmin ? "עודכן" : "העדכון נשמר וממתין לאישור");
      setModal(null);
      onRefresh();
    } catch (e) {
      await showStyledAlert(e.message);
    }
  };

  const toggleStatus = async (r) => {
    if ((r.approvalStatus || "pending") !== "approved") {
      await showStyledAlert("אפשר להפעיל פעילות קבועה רק אחרי אישור סופר אדמין.");
      return;
    }
    try {
      await updateRecurringActivity(r.id, { status: r.status === "active" ? "inactive" : "active" });
      showToast(r.status === "active" ? "בוטל" : "הופעל");
      onRefresh();
    } catch (e) {
      await showStyledAlert(e.message);
    }
  };

  const approveRecurring = async (item) => {
    if (!canApproveRecurring) return;
    try {
      await updateRecurringActivity(item.id, { approvalStatus: "approved", status: item.status === "inactive" ? "active" : item.status || "active" });
      showToast("הפעילות הקבועה אושרה");
      onRefresh();
    } catch (e) {
      await showStyledAlert(e.message);
    }
  };

  const handleDelete = async (r) => {
    if (!await showStyledConfirm("למחוק פעילות קבועה זו?")) return;
    try {
      await deleteRecurringActivity(r.id);
      showToast("פעילות קבועה נמחקה");
      onRefresh();
    } catch (e) {
      await showStyledAlert(e.message);
    }
  };

  return (
    <div>
      {canApproveRecurring && pendingApprovalCount > 0 && (
        <div style={{ marginBottom: 16, background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 14, padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontWeight: 800, color: "#9A3412", marginBottom: 4 }}>ממתינות לאישור</div>
            <div style={{ fontSize: 13, color: "#7C2D12" }}>יש כרגע {pendingApprovalCount} פעילויות קבועות שממתינות לאישור סופר-אדמין.</div>
          </div>
          <ApprovalBadge status="pending" />
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ ...(view === "list" ? btnPrimary : btnSecondary), padding: "6px 12px", fontSize: 13 }} onClick={() => setView("list")}>רשימה</button>
          <button style={{ ...(view === "week" ? btnPrimary : btnSecondary), padding: "6px 12px", fontSize: 13 }} onClick={() => setView("week")}>תצוגת שבוע</button>
        </div>
        {canAddRecurring && <button style={btnPrimary} onClick={() => { setOpenedFromQuickAction(false); setModal("add"); }}><Ic.Plus />חדש</button>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, minmax(0,1fr))", gap: 10, marginBottom: 16 }}>
        <SearchableSelect
          value={volunteerFilter}
          onChange={(nextVolunteerId) => setVolunteerFilter(nextVolunteerId || "all")}
          options={volunteers.filter((v) => v.status === "active")}
          placeholder="כל המתנדבים"
          searchPlaceholder="חיפוש מתנדב לפי שם / טלפון / עיר"
          emptyText="לא נמצאו מתנדבים"
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name || "ללא שם"}
          getOptionKeywords={(option) => [option.firstName, option.lastName, option.phone, option.city].filter(Boolean).join(" ")}
        />
        <SearchableSelect
          value={cityFilter}
          onChange={(nextCity) => setCityFilter(nextCity || "all")}
          options={cityOptions.map((city) => ({ id: city, name: city }))}
          placeholder="כל הערים"
          searchPlaceholder="חיפוש עיר"
          emptyText="לא נמצאו ערים"
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name}
        />
        <select style={{ ...inputStyle, cursor: "pointer" }} value={dayFilter} onChange={(e) => setDayFilter(e.target.value)}>
          <option value="all">כל הימים</option>
          {DAYS_HEB.map((day, index) => <option key={day} value={index}>יום {day}</option>)}
        </select>
        <select style={{ ...inputStyle, cursor: "pointer" }} value={domainFilter} onChange={(e) => setDomainFilter(e.target.value)}>
          <option value="all">כל התחומים</option>
          {activeDomains.map((domain) => <option key={domain.id} value={domain.id}>{domain.name}</option>)}
        </select>
      </div>

      {view === "list" ? (
        <div style={{ background: "white", borderRadius: 14, border: "1px solid #E8E8E8", overflowX: "auto" }}>
          {!isMobile ? <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1040 }}>
            <thead><tr>{["מתנדב", "נעזר", "תחום", "עיר", "יום", "שעה", "משך", "תרומה קבועה", "אישור", "סטטוס", ""].map((h) => <th key={h} style={{ padding: "12px 16px", fontSize: 12, fontWeight: 600, color: "#737373", textAlign: "right", background: "#FAFAFA", borderBottom: "1px solid #E8E8E8" }}>{h}</th>)}</tr></thead>
            <tbody>
              {filteredRecurring.map((r) => (
                <tr key={r.id}>
                  <td style={{ padding: "14px 16px", borderBottom: "1px solid #F5F5F5", fontWeight: 500, fontSize: 14 }}>{volMap[r.volunteerId]?.name || "—"}</td>
                  <td style={{ padding: "14px 16px", borderBottom: "1px solid #F5F5F5", fontSize: 14 }}>{beneficiaries.find((b) => b.id === r.beneficiaryId)?.name || "—"}</td>
                  <td style={{ padding: "14px 16px", borderBottom: "1px solid #F5F5F5" }}>{domMap[r.domainId] ? <DomainTag domain={domMap[r.domainId]} /> : "—"}</td>
                  <td style={{ padding: "14px 16px", borderBottom: "1px solid #F5F5F5", fontSize: 14 }}>{r.city || domMap[r.domainId]?.city || "—"}</td>
                  <td style={{ padding: "14px 16px", borderBottom: "1px solid #F5F5F5", fontSize: 14 }}>יום {DAYS_HEB[r.day]}</td>
                  <td style={{ padding: "14px 16px", borderBottom: "1px solid #F5F5F5", fontSize: 14 }}>{r.time}</td>
                  <td style={{ padding: "14px 16px", borderBottom: "1px solid #F5F5F5", fontSize: 14 }}>{r.duration} שעות</td>
                  <td style={{ padding: "14px 16px", borderBottom: "1px solid #F5F5F5", fontSize: 13 }}>{getActivityContributionSummary(r) || "—"}{getActivityContributionValueTotal(r) > 0 ? <div style={{ color: "#0F766E", fontWeight: 700, marginTop: 4 }}>{formatContributionCurrency(getActivityContributionValueTotal(r))}</div> : null}</td>
                  <td style={{ padding: "14px 16px", borderBottom: "1px solid #F5F5F5" }}><ApprovalBadge status={r.approvalStatus || "pending"} /></td>
                  <td style={{ padding: "14px 16px", borderBottom: "1px solid #F5F5F5" }}><StatusBadge status={r.status} /></td>
                  <td style={{ padding: "14px 16px", borderBottom: "1px solid #F5F5F5" }}>
                    <div style={{ display: "flex", gap: 4 }}>
                      {canApproveRecurring && (r.approvalStatus || "pending") !== "approved" && <button style={btnGhost} onClick={() => approveRecurring(r)} title="אשר"><span style={{ color: "#16A34A" }}><Ic.Check /></span></button>}
                      {canAddRecurring && <button style={btnGhost} onClick={() => setModal({ t: "edit", d: r })}><Ic.Edit /></button>}
                      {canAddRecurring && <button style={btnGhost} onClick={() => toggleStatus(r)}>{r.status === "active" ? <span style={{ color: "#EA580C" }}><Ic.X /></span> : <span style={{ color: "#16A34A" }}><Ic.Check /></span>}</button>}
                      {canAddRecurring && <button style={btnGhost} onClick={() => handleDelete(r)}><span style={{ color: "#DC2626" }}><Ic.Trash /></span></button>}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredRecurring.length === 0 && <tr><td colSpan={11} style={{ textAlign: "center", padding: 32, color: "#A3A3A3" }}>אין פעילויות קבועות</td></tr>}
            </tbody>
          </table> : <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
            {filteredRecurring.map((r) => (
              <div key={r.id} style={{ border: "1px solid #E8E8E8", borderRadius: 10, padding: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <div style={{ fontWeight: 700 }}>{volMap[r.volunteerId]?.name || "—"}</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <ApprovalBadge status={r.approvalStatus || "pending"} />
                    <StatusBadge status={r.status} />
                  </div>
                </div>
                <div style={{ marginBottom: 4 }}>{domMap[r.domainId] ? <DomainTag domain={domMap[r.domainId]} /> : "—"}</div>
                {r.beneficiaryId && <div style={{ fontSize: 12, color: "#525252", marginBottom: 6 }}>נעזר: {beneficiaries.find((b) => b.id === r.beneficiaryId)?.name || "—"}</div>}
                <div style={{ fontSize: 13, color: "#737373", marginBottom: 8 }}>יום {DAYS_HEB[r.day]} | {r.time} | {r.duration} שעות</div>
                {getActivityContributionSummary(r) && <div style={{ fontSize: 12, color: "#9A3412", marginBottom: 8 }}>{getActivityContributionSummary(r)}{getActivityContributionValueTotal(r) > 0 ? ` · ${formatContributionCurrency(getActivityContributionValueTotal(r))}` : ""}</div>}
                <div style={{ display: "flex", gap: 8 }}>
                  {canApproveRecurring && (r.approvalStatus || "pending") !== "approved" && <button style={btnSecondary} onClick={() => approveRecurring(r)}>אישור</button>}
                  {canAddRecurring && <button style={btnSecondary} onClick={() => setModal({ t: "edit", d: r })}><Ic.Edit />עריכה</button>}
                  {canAddRecurring && <button style={btnSecondary} onClick={() => toggleStatus(r)}>{r.status === "active" ? "השבת" : "הפעל"}</button>}
                  {canAddRecurring && <button style={{ ...btnSecondary, color: "#B91C1C", borderColor: "#FECACA", background: "#FEF2F2" }} onClick={() => handleDelete(r)}>מחיקה</button>}
                </div>
              </div>
            ))}
            {filteredRecurring.length === 0 && <div style={{ textAlign: "center", padding: 24, color: "#A3A3A3" }}>אין פעילויות קבועות</div>}
          </div>}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 8 }}>
          {DAYS_HEB.map((day, idx) => {
            const dayR = filteredRecurring.filter((r) => Number(r.day) === idx && r.status === "active");
            return (
              <div key={idx} style={{ background: "white", borderRadius: 12, border: "1px solid #E8E8E8", overflow: "hidden" }}>
                <div style={{ padding: 10, textAlign: "center", fontWeight: 600, fontSize: 13, background: "#FAFAFA", borderBottom: "1px solid #E8E8E8" }}>יום {day}</div>
                <div style={{ padding: 8, minHeight: 140, display: "flex", flexDirection: "column", gap: 4 }}>
                  {dayR.map((r) => {
                    const dom = domMap[r.domainId];
                    return (
                      <div key={r.id} style={{ padding: "6px 8px", borderRadius: 6, fontSize: 11, color: "white", fontWeight: 500, lineHeight: 1.3, background: dom?.color || "#737373" }}>
                        {volMap[r.volunteerId]?.name?.split(" ")[0] || "ללא שם"}
                        <br />
                        {(r.city || dom?.city) ? `${r.city || dom?.city} · ` : ""}
                        {r.time} ({r.duration}ש')
                      </div>
                    );
                  })}
                  {dayR.length === 0 && <div style={{ color: "#A3A3A3", fontSize: 12, textAlign: "center", padding: 12 }}>—</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(modal === "add" || modal?.t === "edit") && (
        <RecFormModal volunteers={volunteers} beneficiaries={beneficiaries} domains={domains} contributionTypes={contributionTypes} item={modal?.d} onClose={() => { setModal(null); setOpenedFromQuickAction(false); }} onSave={modal === "add" ? handleAdd : handleEdit} />
      )}
    </div>
  );
}

function RecFormModal({ volunteers, beneficiaries = [], domains, contributionTypes = [], item, onClose, onSave }) {
  const isMainCategory = (d) => d?.isMainCategory !== false;
  const activeDomains = useMemo(() => domains.filter((d) => d.active !== false), [domains]);
  const domainById = useMemo(() => {
    const map = {};
    activeDomains.forEach((d) => { map[d.id] = d; });
    return map;
  }, [activeDomains]);
  const mainDomains = useMemo(
    () => activeDomains.filter((d) => isMainCategory(d)),
    [activeDomains]
  );
  const childrenByMainId = useMemo(() => {
    const map = {};
    activeDomains.forEach((d) => {
      const parentId = (d.parentDomainId || "").trim();
      if (!parentId) return;
      if (!map[parentId]) map[parentId] = [];
      map[parentId].push(d);
    });
    return map;
  }, [activeDomains]);
  const resolveMainDomainId = (domainId) => {
    const current = domainById[domainId];
    if (!current) return "";
    const parentId = (current.parentDomainId || "").trim();
    if (!parentId) return current.id;
    return domainById[parentId] ? parentId : current.id;
  };
  const initialDomainId = (item?.domainId || "").trim();
  const defaultMainDomainId = resolveMainDomainId(initialDomainId) || mainDomains[0]?.id || activeDomains[0]?.id || "";
  const defaultDomainId = initialDomainId || defaultMainDomainId;
  const cityOptions = useMemo(() => Array.from(new Set(
    [
      ...activeDomains.map((domain) => (domain?.city || "").trim()),
      ...volunteers.map((volunteer) => (volunteer?.city || "").trim()),
    ].filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, "he")), [activeDomains, volunteers]);

  const [f, setF] = useState(item ? { ...item, beneficiaryId: item?.beneficiaryId || "", city: item?.city || "", contributions: normalizeActivityContributions(item?.contributions) } : { volunteerId: "", beneficiaryId: "", domainId: defaultDomainId, city: "", day: 0, time: "09:00", duration: 1, startDate: fmtDate(new Date()), endDate: "", contributions: [] });
  const [mainDomainId, setMainDomainId] = useState(defaultMainDomainId);
  const [saving, setSaving] = useState(false);
  const u = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const activeContributionTypes = useMemo(() => (contributionTypes || []).filter((item) => item.active !== false), [contributionTypes]);
  const childDomainOptions = useMemo(
    () => childrenByMainId[mainDomainId] || [],
    [childrenByMainId, mainDomainId]
  );
  const selectedChildId = childDomainOptions.some((d) => d.id === f.domainId) ? f.domainId : "";

  useEffect(() => {
    if (!item) return;
    setMainDomainId(resolveMainDomainId(item.domainId) || defaultMainDomainId);
  }, [item, defaultMainDomainId]);

  const submit = async () => {
    setSaving(true);
    await onSave(f);
    setSaving(false);
  };

  return (
    <Modal title={item ? "עריכת פעילות קבועה" : "יצירת פעילות קבועה"} onClose={onClose} footer={<><button style={btnPrimary} onClick={submit} disabled={saving}><Ic.Check />{saving ? "שומר..." : "שמירה"}</button><button style={btnSecondary} onClick={onClose}>ביטול</button></>}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ marginBottom: 16 }}><label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525252", marginBottom: 6 }}>מתנדב מבצע</label><SearchableSelect value={f.volunteerId} onChange={(nextVolunteerId) => u("volunteerId", nextVolunteerId)} options={volunteers.filter((v) => v.status === "active")} placeholder="בחר מתנדב" searchPlaceholder="חיפוש מתנדב לפי שם / טלפון / עיר" emptyText="לא נמצאו מתנדבים" getOptionValue={(option) => option.id} getOptionLabel={(option) => option.name || "ללא שם"} getOptionKeywords={(option) => [option.firstName, option.lastName, option.phone, option.city].filter(Boolean).join(" ")} /></div>
        <div style={{ marginBottom: 16 }}><label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525252", marginBottom: 6 }}>קטגוריה ראשית</label><select style={{ ...inputStyle, cursor: "pointer" }} value={mainDomainId} onChange={(e) => { const nextMainId = e.target.value; setMainDomainId(nextMainId); u("domainId", nextMainId); }}>{mainDomains.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></div>
      </div>
      <div style={{ marginBottom: 16 }}><label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525252", marginBottom: 6 }}>נעזר משויך לפעילות</label><SearchableSelect value={f.beneficiaryId || ""} onChange={(nextBeneficiaryId) => u("beneficiaryId", nextBeneficiaryId)} options={beneficiaries.filter((beneficiary) => beneficiary.active !== false)} placeholder="בחר נעזר" searchPlaceholder="חיפוש נעזר לפי שם / טלפון / עיר" emptyText="לא נמצאו נעזרים" getOptionValue={(option) => option.id} getOptionLabel={(option) => option.name || "ללא שם"} getOptionKeywords={(option) => [option.phone, option.secondaryPhone, option.city, option.address].filter(Boolean).join(" ")} /></div>
      <div style={{ marginBottom: 16 }}><label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525252", marginBottom: 6 }}>תת קטגוריה</label><select style={{ ...inputStyle, cursor: "pointer" }} value={selectedChildId} onChange={(e) => u("domainId", e.target.value || mainDomainId)}><option value="">ללא תת קטגוריה (ראשית בלבד)</option>{childDomainOptions.map((d) => <option key={d.id} value={d.id}>{d.name}{(d.city || "").trim() ? ` (${d.city})` : ""}</option>)}</select></div>
      <div style={{ marginBottom: 16 }}><label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525252", marginBottom: 6 }}>עיר</label><AutocompleteTextInput value={f.city || ""} onChange={(nextCity) => u("city", nextCity)} options={cityOptions} placeholder="לדוגמה: באר שבע" emptyText="אין ערים תואמות" /></div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ marginBottom: 16 }}><label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525252", marginBottom: 6 }}>יום</label><select style={{ ...inputStyle, cursor: "pointer" }} value={f.day} onChange={(e) => u("day", Number(e.target.value))}>{DAYS_HEB.map((d, i) => <option key={i} value={i}>יום {d}</option>)}</select></div>
        <div style={{ marginBottom: 16 }}><label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525252", marginBottom: 6 }}>שעה</label><input style={inputStyle} type="time" value={f.time} onChange={(e) => u("time", e.target.value)} /></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <div style={{ marginBottom: 16 }}><label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525252", marginBottom: 6 }}>משך</label><select style={{ ...inputStyle, cursor: "pointer" }} value={f.duration} onChange={(e) => u("duration", Number(e.target.value))}>{[0.5, 1, 1.5, 2, 2.5, 3, 4, 5].map((h) => <option key={h} value={h}>{h} שעות</option>)}</select></div>
        <div style={{ marginBottom: 16 }}><label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525252", marginBottom: 6 }}>תאריך התחלה</label><input style={inputStyle} type="date" value={f.startDate} onChange={(e) => u("startDate", e.target.value)} /></div>
        <div style={{ marginBottom: 16 }}><label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525252", marginBottom: 6 }}>תאריך סיום</label><input style={inputStyle} type="date" value={f.endDate || ""} onChange={(e) => u("endDate", e.target.value)} /></div>
      </div>
      <div style={{ marginBottom: 16, marginTop: -4, fontSize: 12, color: "#737373", lineHeight: 1.6 }}>
        תאריך סיום עוצר יצירה של פעילויות חדשות מהתאריך שנקבע ואילך, בלי למחוק את ההיסטוריה שכבר נוצרה.
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525252" }}>תרומה קבועה</label>
          <button type="button" style={{ ...btnSecondary, padding: "6px 10px", fontSize: 12 }} onClick={() => u("contributions", [...(Array.isArray(f.contributions) ? f.contributions : []), { type: activeContributionTypes[0]?.name || "", quantity: 1, unitValue: activeContributionTypes[0]?.unitValue ?? null }])}>
            <Ic.Plus /> הוסף תרומה
          </button>
        </div>
        {(Array.isArray(f.contributions) ? f.contributions : []).length === 0 && (
          <div style={{ fontSize: 12, color: "#737373", padding: "10px 12px", borderRadius: 10, border: "1px dashed #D4D4D8" }}>ללא תרומה קבועה</div>
        )}
        <div style={{ display: "grid", gap: 8 }}>
          {(Array.isArray(f.contributions) ? f.contributions : []).map((row, idx) => (
            <div key={`${row.type || "contribution"}-${idx}`} style={{ display: "grid", gridTemplateColumns: "1.5fr 0.8fr 0.8fr auto", gap: 8, alignItems: "center" }}>
              <select style={{ ...inputStyle, cursor: "pointer" }} value={row.type || ""} onChange={(e) => u("contributions", f.contributions.map((item, itemIdx) => itemIdx === idx ? { ...item, type: e.target.value } : item))}>
                <option value="">בחר סוג תרומה</option>
                {activeContributionTypes.map((type) => <option key={type.id} value={type.name}>{type.name}</option>)}
              </select>
              <input type="number" min="1" style={inputStyle} value={row.quantity ?? 1} onChange={(e) => u("contributions", f.contributions.map((item, itemIdx) => itemIdx === idx ? { ...item, quantity: Number(e.target.value) || 0 } : item))} />
              <input type="number" min="0" step="0.01" style={inputStyle} value={row.unitValue ?? ""} onChange={(e) => u("contributions", f.contributions.map((item, itemIdx) => itemIdx === idx ? { ...item, unitValue: e.target.value === "" ? null : Number(e.target.value) } : item))} placeholder="שווי" />
              <button type="button" style={{ ...btnGhost, color: "#DC2626" }} onClick={() => u("contributions", f.contributions.filter((_, itemIdx) => itemIdx !== idx))}><Ic.Trash /></button>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}
